import React from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import ComponentHeader from '../ecap/ComponentHeader';
import BarGroupHorizontal from './utility/BarGroupHorizontal';
import BarGroupVertical from './utility/BarGroupVertical';
import { riasecScoreCopy } from '../../copy/user-dashboard/riasec-score-copy.json';
import { updateUserProgress, getUserDashboardInfo } from '../../store/actions';
import { getOnboardingState, getAuthState } from '../../store/selectors';

const learnMoreStyles = {
  color: (props) => ({
    backgroundColor: props.tenant ? props.tenant.primary : 'white',
    borderColor: props.tenant ? props.tenant.primary : 'black',

    '&:hover': {
      backgroundColor: 'white',
      border: props.tenant
        ? `2px solid ${props.tenant.primary}`
        : '2px solid black',
      color: props.tenant ? props.tenant.primary : 'black',
      borderStyle: 'solid',
    },
  }),
};

class RiasecScore extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: riasecScoreCopy.labels,
    };
  }

  getScoreValue = (name) => {
    const {
      gameRChoices,
      gameRChoicesRetry,
      gameIChoices,
      gameIChoicesRetry,
      gameAChoices,
      gameAChoicesRetry,
      gameSChoices,
      gameSChoicesRetry,
      gameEChoices,
      gameEChoicesRetry,
      gameCChoices,
      gameCChoicesRetry,
    } = this.props.location.pathname.includes('user-profile')
      ? this.props.userProfile.gameChoices[0]
      : this.props.onboarding.userData.user;

    switch (name) {
      case riasecScoreCopy.labels[0].name:
        return gameRChoicesRetry || gameRChoices;
      case riasecScoreCopy.labels[1].name:
        return gameIChoicesRetry || gameIChoices;
      case riasecScoreCopy.labels[2].name:
        return gameAChoicesRetry || gameAChoices;
      case riasecScoreCopy.labels[3].name:
        return gameSChoicesRetry || gameSChoices;
      case riasecScoreCopy.labels[4].name:
        return gameEChoicesRetry || gameEChoices;
      case riasecScoreCopy.labels[5].name:
        return gameCChoicesRetry || gameCChoices;
      default:
        return 0;
    }
  };

  componentDidMount() {
    const riasecData = riasecScoreCopy.labels;
    const formattedData = riasecData.map((category) => {
      return {
        name: category.name,
        value: this.getScoreValue(category.name),
      };
    });
    this.setState({
      data: formattedData,
    });
  }

  showGraph = (sortedData) => {
    const thickness = 40;
    const spacing = 150;
    const vThickness = 32;
    const vSpacing = 85;
    if (this.props.isTablet && !this.props.isMobile) {
      return (
        <div className="graph-container tablet">
          <svg
            width="550"
            height="400"
            role="img"
            aria-describedby="riasec-graph-description"
          >
            <Translation>
              {(t) => (
                <title>
                  {t('{{adjective}} Score', { adjective: 'RIASEC' })}
                </title>
              )}
            </Translation>
            <g className="chart">
              <BarGroupHorizontal
                data={sortedData}
                thickness={vThickness}
                spacing={vSpacing - 3}
                tenant={this.props.tenant}
                tablet
              />
            </g>
          </svg>
        </div>
      );
    }
    if (this.props.isMobile) {
      return (
        <div className="graph-container mobile">
          <svg
            width="500"
            height="575"
            role="img"
            aria-describedby="riasec-graph-description"
          >
            <Translation>
              {(t) => (
                <title>
                  {t('{{adjective}} Score', { adjective: 'RIASEC' })}
                </title>
              )}
            </Translation>
            <g className="chart">
              <BarGroupVertical
                data={sortedData}
                thickness={vThickness}
                spacing={vSpacing}
                tenant={this.props.tenant}
              />
            </g>
          </svg>
        </div>
      );
    }
    return (
      <div className="graph-container">
        <svg
          width="900"
          height="400"
          role="img"
          aria-describedby="riasec-graph-description"
        >
          <Translation>
            {(t) => (
              <title>{t('{{adjective}} Score', { adjective: 'RIASEC' })}</title>
            )}
          </Translation>
          <g className="chart">
            <BarGroupHorizontal
              data={sortedData}
              thickness={thickness}
              spacing={spacing}
              tenant={this.props.tenant}
            />
          </g>
        </svg>
      </div>
    );
  };

  sortData = (data) => {
    return data.sort((a, b) => {
      return b.value - a.value;
    });
  };

  yourStrengths = (data, isLabel) => {
    return data.slice(0, 3).map((item) => {
      if (isLabel) {
        return item.name;
      }
      return item.name.slice(0, 1);
    });
  };

  retakeGame = () => {
    this.props.history.push('game-landing?retry=true');
  };

  learnMore = async () => {
    const learnMoreLink = 'https://me3.careers/#sectionTheScience';
    if (!this.props.onboarding.userData.userProgress.learned_about_riasec) {
      await this.props.updateUserProgress(this.props.auth.jwtToken, {
        learned_about_riasec: true,
      });
    }
    window.open(learnMoreLink, '_blank');
  };

  /* eslint-disable react/jsx-one-expression-per-line */
  render() {
    const sortedData = this.sortData(this.state.data);
    const primaryColor = {
      color: this.props.tenant.primary,
    };
    return (
      <Translation>
        {(t) => (
          <div id="dashboard-riasec-score">
            {!this.props.location.pathname.includes('user-profile') && (
              <div className="game-link">
                <p
                  role="link"
                  style={primaryColor}
                  onClick={this.retakeGame}
                  onKeyDown={(e) => {
                    if (['Enter'].includes(e.key)) this.retakeGame();
                  }}
                  tabIndex="0"
                >
                  {t('Retake the Game', { context: 'Link' })}{' '}
                  <span aria-hidden>{'>'}</span>
                </p>
              </div>
            )}
            <ComponentHeader
              parent="dashboard"
              headerText={
                this.props.location.pathname.includes('user-profile')
                  ? t('{{adjective}} Score', { adjective: 'RIASEC' })
                  : t('My {{adjective}} Score', { adjective: 'RIASEC' })
              }
              tenant={this.props.tenant}
              reduced
            />
            <div className="top-section" id="riasec-graph-description">
              <p className="explanation">
                {t(
                  'By clicking on the photos that were appealing to you, {{applicationName}} calculated your {{adjective}} score which represents your levels of interest in data, things, people, and ideas.  A match between one’s interest code ({{adjective}} score) and related careers has been linked to greater career stability and job satisfaction.',
                  { applicationName: 'me3', adjective: 'RIASEC' }
                )}
              </p>
              <div className="strengths-box">
                <p className="strengths-label">{t('Your Strengths:')}</p>
                <p className="strengths-values" aria-hidden>
                  {this.yourStrengths(this.state.data, false)}
                </p>
                <p className="visually-hidden">
                  {this.yourStrengths(this.state.data, true)}
                </p>
              </div>
            </div>
            {this.showGraph(sortedData)}
            <button
              className={[
                this.props.classes.color,
                'button--primary',
                'learn-more-button',
              ].join(' ')}
              onClick={this.learnMore}
              type="button"
              aria-label="Learn more about RIASEC."
            >
              {t('Learn More', { context: 'Link' })}
            </button>
          </div>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  const userProfile = state.admin.userProfileInfo;

  return { onboarding, auth, userProfile };
};

export default connect(mapStateToProps, {
  updateUserProgress,
  getUserDashboardInfo,
})(withStyles(learnMoreStyles)(RiasecScore));
